import { useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"
import { AVATAR_SIZE } from "../lib/style-resolver"

/**
 * Avatar renderer — displays a circular avatar image with fallback initials.
 *
 * JSON shape:
 * {
 *   "type": "avatar",
 *   "src": "https://...",
 *   "alt": "User Name",
 *   "size": "md",           // xs, sm, md, lg, xl or number
 *   "fallback": "JD",       // initials when image fails
 *   "className": "..."
 * }
 */
export function AvatarRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()

  const src = element.src ? String(resolveSymbol(element.src) ?? "") : ""
  const alt = element.alt ? String(resolveSymbol(element.alt) ?? "") : ""
  const fallback = element.fallback ? String(resolveSymbol(element.fallback) ?? "") : ""
  const sizeToken = element.size ?? element.options?.size ?? "md"

  const sizeClass = typeof sizeToken === "string"
    ? AVATAR_SIZE[sizeToken] ?? AVATAR_SIZE.md
    : ""

  const inlineStyle: React.CSSProperties = {}
  if (typeof sizeToken === "number") {
    inlineStyle.width = sizeToken
    inlineStyle.height = sizeToken
  }
  // Apply styling props (marginLeft for stacked avatars, border)
  const styling = element.styling as Record<string, unknown> | undefined
  if (styling?.marginLeft) inlineStyle.marginLeft = `${styling.marginLeft}px`
  if (styling?.border) {
    const border = styling.border as Record<string, unknown>
    if (border.width) {
      inlineStyle.borderWidth = `${border.width}px`
      inlineStyle.borderStyle = "solid"
      inlineStyle.borderColor = border.color ? String(resolveSymbol(String(border.color)) ?? "#fff") : "#fff"
    }
  }
  const hasStyle = Object.keys(inlineStyle).length > 0

  // Derive initials from alt text if no fallback specified
  const initials = fallback || alt.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()

  const [imgError, setImgError] = useState(false)

  if (src && !imgError) {
    return (
      <span className={cn("relative inline-flex shrink-0 overflow-hidden rounded-full", sizeClass, element.className)} style={hasStyle ? inlineStyle : undefined}>
        <img
          src={src}
          alt={alt}
          className="aspect-square h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      </span>
    )
  }

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted",
        sizeClass,
        element.className,
      )}
      style={hasStyle ? inlineStyle : undefined}
    >
      <span className="font-medium text-muted-foreground">{initials}</span>
    </span>
  )
}
